import React, { useState } from "react";
import { Box, Typography, useTheme, Menu, MenuItem, InputBase, ListItemIcon } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { RAIL_POPUP_BG, RAIL_POPUP_HOVER } from "../../../Navbar/railPopoverStyles";

function FilterDropdown({ options, value, onChange, icons, width = 120, searchable = false }) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuWidth, setMenuWidth] = useState(0);
  const [query, setQuery] = useState("");
  const open = Boolean(anchorEl);
  const ValueIcon = icons?.[value]?.Icon;
  const filteredOptions = searchable && query
    ? options.filter((opt) => opt.toLowerCase().includes(query.toLowerCase()))
    : options;

  const closeMenu = () => {
    setAnchorEl(null);
    setQuery("");
  };

  return (
    <>
      <Box
        onClick={(e) => {
          setMenuWidth(e.currentTarget.offsetWidth);
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          width,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "8px",
          px: 1.25,
          py: 0.625,
          fontSize: "0.8125rem",
          color: "text.secondary",
          cursor: "pointer",
          userSelect: "none",
          outline: "none",
          "&:focus, &:focus-visible": { outline: "none" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.625, flex: 1, minWidth: 0 }}>
          {ValueIcon && <ValueIcon sx={{ fontSize: 14, flexShrink: 0, color: icons[value].color }} />}
          <Box component="span" sx={{ minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </Box>
        </Box>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 16,
            flexShrink: 0,
            ml: 0.5,
            transition: "transform 0.15s ease",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              // Captured once at open-time (menuWidth), not read live off anchorEl —
              // anchorEl gets set to null immediately on close, but MUI keeps this Paper
              // mounted through its exit transition, so a live anchorEl?.offsetWidth read
              // would evaluate to undefined mid-animation and snap the box to auto-width
              // right as it's also scaling down, causing a visible resize glitch.
              width: menuWidth || anchorEl?.offsetWidth,
              ...(searchable
                ? {
                    maxHeight: 260,
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                  }
                : {}),
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.palette.mode === "dark" ? "0 8px 24px rgba(0,0,0,0.4)" : "0 8px 24px rgba(0,0,0,0.12)",
              backgroundColor: RAIL_POPUP_BG(theme),
              py: 0.5,
            },
          },
        }}
      >
        {searchable && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              position: "sticky",
              top: 0,
              zIndex: 1,
              backgroundColor: RAIL_POPUP_BG(theme),
              mx: 1,
              mb: 2,
              px: 1.25,
              py: 0.625,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.divider}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <SearchIcon sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }} />
            <InputBase
              autoFocus
              placeholder="Search teams…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{ fontSize: "0.8125rem", flex: 1, color: "text.primary" }}
            />
          </Box>
        )}
        {filteredOptions.length === 0 && (
          <Typography sx={{ px: 1.75, py: 1, fontSize: "0.8125rem", color: "text.secondary" }}>
            No teams found
          </Typography>
        )}
        {filteredOptions.map((opt) => {
          const OptIcon = icons?.[opt]?.Icon;
          return (
            <MenuItem
              key={opt}
              selected={opt === value}
              onClick={() => {
                onChange(opt);
                closeMenu();
              }}
              sx={{
                fontSize: "0.8125rem",
                fontWeight: opt === value ? 600 : 400,
                py: 0.75,
                px: 1.25,
                mb: 0.25,
                borderRadius: "8px",
                mx: 0.5,
                color: "text.primary",
                outline: "none",
                "&:focus, &:focus-visible": { outline: "none", backgroundColor: RAIL_POPUP_HOVER(theme) },
                "&:hover": {
                  backgroundColor: RAIL_POPUP_HOVER(theme),
                },
                "&.Mui-selected": {
                  backgroundColor: RAIL_POPUP_HOVER(theme),
                },
                "&.Mui-selected:hover": {
                  backgroundColor: RAIL_POPUP_HOVER(theme),
                },
              }}
            >
              {OptIcon && (
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <OptIcon sx={{ fontSize: 14, color: icons[opt].color }} />
                </ListItemIcon>
              )}
              <Box component="span" sx={{ minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {opt}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default FilterDropdown;
